import type {
  OrderAppCtaType,
  OrderAppProductVariantsBusinessDetailsQuery,
} from '@/graphql/orderApp/api';
import type { ProductCardFragment } from '@/graphql/saleor/api';
import type { ProductCardFragmentWithVariantCta } from '@/hooks/useFetchProductsWithCta';
import type { Maybe } from '@/lib/types';

/**
 * Adds a CTA to each product, if applicable.
 *
 * @param productVariants - List of product variants containing CTA.
 * @param products - List of products without CTA.
 * @returns - List of products with CTA added, if applicable.
 */
export const unionProductsAndVariantCta = (
  productVariants: OrderAppProductVariantsBusinessDetailsQuery['productVariants'],
  products: ProductCardFragment[],
) => {
  if (productVariants && productVariants.length > 0) {
    const variantMap = new Map(
      productVariants.map((variant) => [variant.sku, variant.variantCTA]),
    );

    // Always return all products, but attach variantCTA if available
    return products.map((product) => {
      const sku = product.defaultVariant?.sku;
      const variantCTA = sku ? variantMap.get(sku) : undefined;

      return {
        ...product,
        variantCTA: variantCTA,
      } as ProductCardFragmentWithVariantCta;
    });
  }

  // If no productVariants, return products without setting variantCTA
  return products.map((product) => {
    return {
      ...product,
      variantCTA: undefined,
    } as ProductCardFragmentWithVariantCta;
  });
};

export const findProductCtaFromBusinessData = (
  orderAppData: Maybe<OrderAppProductVariantsBusinessDetailsQuery>,
  sku?: Maybe<string>,
): OrderAppCtaType | undefined => {
  if (orderAppData?.productVariants) {
    for (const orderAppItem of orderAppData.productVariants) {
      if (sku === orderAppItem.sku) {
        return orderAppItem.variantCTA;
      }
    }
  }
};

export const findProductCta = (
  strapSizeDatas?: Record<string, any>,
  sku?: Maybe<string>,
): OrderAppCtaType | undefined => {
  if (!strapSizeDatas?.variants) {
    return undefined;
  }

  if (sku && strapSizeDatas.variants[sku]) {
    return strapSizeDatas.variants[sku].call_to_action as OrderAppCtaType;
  }

  return undefined;
};

export const getMostRelevantProductCta = (
  strapSizeDatas?: Record<string, any>,
  sku?: Maybe<string>,
): OrderAppCtaType | undefined => {
  // Priority order of CTAs
  const ctaPriority = ['ORDER', 'PRE_ORDER', 'NOTIFY', 'NO_CTA'];

  if (!strapSizeDatas?.variants) {
    return undefined;
  }

  // Search for CTAs in order of priority
  for (const ctaType of ctaPriority) {
    for (const variantSku in strapSizeDatas.variants) {
      const variant = strapSizeDatas.variants[variantSku];
      if (variant && variant.call_to_action === ctaType) {
        return variant.call_to_action as OrderAppCtaType;
      }
    }
  }

  // Fallback - If sku is provided, check if it exists in variants
  if (sku && strapSizeDatas.variants[sku]) {
    return strapSizeDatas.variants[sku].call_to_action as OrderAppCtaType;
  }

  return undefined;
};
